/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { SecurityService } from '@wsphere/staff/web';
import { Claim } from '@wsphere/warranties/domain';
import { concat, from, map, of, shareReplay, switchMap } from 'rxjs';
import { CLAIM_ITEM_DECISION_CONFIG } from '../../config/claim-item-decision.config';
import { ClaimService } from '../../services';

type Decision = Claim.ClaimItem.Decision.Model & { reasonLabel: string | null };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-adjudicate',
  templateUrl: './claim-item-adjudicate.component.html',
  standalone: false,
})
export class ClaimItemAdjudicateComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private securitySvc: SecurityService,
    private claimSvc: ClaimService
  ) {}

  readonly claim$ = this.claimSvc.getClaim();
  readonly item$ = this.route.data.pipe(map(data => data['item'] as Claim.ClaimItem.Model));

  /** Null while loading or when the job has no decisions, so switching jobs never shows the previous history. */
  readonly decisions$ = this.item$.pipe(
    switchMap(item => concat(of(null), from(this.loadDecisions(item)))),
    // Read by the history and the eligibility card; one load serves both.
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly config = CLAIM_ITEM_DECISION_CONFIG;

  /** Eligibility is part of the new claims flow. */
  readonly usesNewFlow = Claim.Authorization.usesNewFlow;

  open = Claim.Status.OPEN;
  draft = Claim.ClaimItem.Status.DRAFT;
  redrafted = Claim.ClaimItem.Decision.Action.REDRAFTED;

  decisionTheme: { [key in Claim.ClaimItem.Decision.Action]: ThemeColor } = {
    APPROVED: 'success',
    REJECTED: 'danger',
    REDRAFTED: 'default',
  };

  private reasons?: Promise<Claim.ClaimItem.Decision.Reasons | null>;

  canManageAdjudication$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_AdjudicateClaimItem', permissionKey);
    })
  );

  readyForAdjudication(claim: Claim.Model | null | undefined, item: Claim.ClaimItem.Model) {
    return Claim.ClaimItem.readyForAdjudication(claim, item);
  }

  navigateTo(path?: string) {
    this.router.navigate([`../${path}`], { relativeTo: this.route });
  }

  formatAmount(amount: number | null | undefined) {
    return amount === null || amount === undefined ? '-' : '$' + (amount / 100).toFixed(2);
  }

  /** What the latest decision approved, in cents; null unless the latest decision is an approval. */
  approvedAmount(decisions: { latest: Decision } | null) {
    return decisions?.latest.action === Claim.ClaimItem.Decision.Action.APPROVED
      ? decisions.latest.authorizedAmount
      : null;
  }

  private async loadDecisions(item?: Claim.ClaimItem.Model | null) {
    if (!item) return null;

    const response = await this.claimSvc.listItemDecisions(item.claimId, item.id).catch(() => null);
    if (!response?.items?.length) return null;

    const reasons = await this.getReasons();
    const items: Decision[] = response.items.map(decision => ({
      ...decision,
      reasonLabel: this.reasonLabel(decision, reasons),
    }));

    // The server sends newest first.
    return { latest: items[0], items };
  }

  private getReasons() {
    this.reasons ??= this.claimSvc.getDecisionReasons().catch(() => {
      this.reasons = undefined;
      return null;
    });
    return this.reasons;
  }

  /** The configured label, or the raw code when it's no longer configured. */
  private reasonLabel(decision: Claim.ClaimItem.Decision.Model, reasons: Claim.ClaimItem.Decision.Reasons | null) {
    if (!decision.reasonCode) return null;
    if (!reasons || decision.action === Claim.ClaimItem.Decision.Action.REDRAFTED) return decision.reasonCode;

    const reason = Claim.ClaimItem.Decision.findDecisionReason(reasons, decision.action, decision.reasonCode);
    return reason?.label ?? decision.reasonCode;
  }
}
